import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { common, economic, input } from "../../../../../i18n";
import Grid2 from "@mui/material/Unstable_Grid2";
import LabelInput from "../../../../../components/input/LabelInput";
import { useEffect, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { EcoEfficiencyContext } from "../../../../../contexts/EcoEfficiencyContext";
import { GridColDef } from "@mui/x-data-grid";
import { AppDataGrid } from "../../../../../components/datagrid/AppDataGrid";
import { tableStyle } from "../../../../emission/_components/data-table/EmissionDataTableStyle";
import { EconomicIndicatorComponent } from "../../../../../models/dto/response/ResponseCreateEconomicIndicators";
import { NUMERIC_WITH_COMMA_REGEX } from "../../../../../constants/form";
import { Controller, useForm } from "react-hook-form";
import {
  EcoEffActualRequest,
  EconomicIndicatorValueResult,
  MonthlyComponentEntry,
} from "../../../../../models/dto/response/ResponseEcoEfficiency";
import { EcoEfficiencyService } from "../../../../../service/eco-efficiency.service";
import alertService from "../../../../../components/alert/alertService";
import ActualDataMonth from "./ActualDataMonth";
import dayjs from "dayjs";
import NumberFormatterUtils from "../../../../../utils/numberFormatterUtils";
import { textFieldEndAlignWithFloatingHelper } from "../plan-setting/PlanSettingPercentChangesTable";
import { NumericFormat } from "react-number-format";

export const getFieldConfig = (
  rowId: string,
  rowData: any,
  {
    t,
    setValue,
    getValues,
    updateValue,
    errors,
  }: {
    t: (key: string) => string;
    setValue: (name: string, value: any) => void;
    getValues: (name: string) => any;
    updateValue?: (row: any, value: any) => void;
    errors: Record<string, any>;
  },
) => ({
  pattern: {
    value: NUMERIC_WITH_COMMA_REGEX,
    message: t("Numbers-Only"),
  },
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
    // const value = event.target.value;
    // setValue(rowId, value.replaceAll(",", ""));

    const raw = event.target.value.replace(/,/g, "");

    const formatted = NumberFormatterUtils.formatNumberWithComma(raw);

    setValue(rowId, formatted);
  },
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
    const value = getValues(rowId).replaceAll(",", "");

    if (
      !errors[rowId] &&
      value != null &&
      value !== "" &&
      !isNaN(Number(value))
    ) {
      if (updateValue) updateValue(rowData, value);
    }
  },
});

export const ActualData = () => {
  const { t, i18n } = useTranslation([common, input, economic], {
    nsMode: "fallback",
  });

  const {
    editData,
    section,
    setEcoEfficiency,
    setEditData,
    getEcoEfficiency,
    setModalIsOpen,
    setSection,
    ecoEfficiency,
  } = EcoEfficiencyContext();

  const [periodType, setPeriodType] = useState<"monthly" | "yearly">("yearly");

  const handlePeriodTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    setPeriodType(value as "monthly" | "yearly");

    onSubmit(value as "monthly" | "yearly");
  };

  const {
    setValue,
    getValues,
    control,
    watch,
    formState: { errors },
  } = useForm({
    reValidateMode: "onChange",
    mode: "all",
  });

  const [monthly, setMonthly] = useState<MonthlyComponentEntry[]>([]);

  const [rows, setRows] = useState<EconomicIndicatorComponent[]>([]);

  const columns: GridColDef<EconomicIndicatorComponent>[] = [
    {
      field: "id",
      headerName: t("ID"),
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return `PV-${
          params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
        }`;
      },
    },
    {
      field: "baseYear",
      headerName: t("Year"),
      minWidth: 80,
      align: "left",
      valueGetter: () => dayjs(editData!.startDate).year(),
    },
    {
      field: "nameTh",
      headerName: t("Indicator-Name-Table"),
      headerAlign: "left",
      minWidth: 140,
      flex: 1,
      valueGetter: () =>
        i18n.language === "th"
          ? editData?.economicindicatorsRef.nameTh
          : editData?.economicindicatorsRef.nameEn,
    },
    {
      field: "nameEn",
      headerName: t("Components"),
      headerAlign: "left",
      minWidth: 140,
      flex: 1,
      valueGetter: (value, row) =>
        i18n.language === "th" ? row.nameTh : row.nameEn,
    },
    {
      field: "",
      headerName: `${t("Amount-of-Product-Value-Indicator")}`,
      disableColumnMenu: true,
      headerAlign: "left",
      align: "left",
      flex: 1,
      minWidth: 200,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              ...textFieldEndAlignWithFloatingHelper,
            }}
          >
            {/* <TextField
              key={params.row._id}
              {...register(
                params.row._id,
                getFieldConfig(params.row._id, params.row, {
                  t,
                  setValue,
                  getValues,
                  updateValue,
                  errors,
                })
              )}
              onKeyDown={(event) => {
                event.stopPropagation();
              }}
              onKeyUp={async (event: React.KeyboardEvent) => {
                switch (event.key) {
                  case "Enter":
                    (document.activeElement as HTMLElement).blur();
                    break;
                  default:
                    break;
                }
              }}
              helperText={
                errors[params.row._id] &&
                errors[params.row._id]?.message?.toString()
              }
              error={errors[params.row._id] && true}
              type="text"
              sx={{
                textAlign: "end",
              }}
              placeholder={t("Enter-Value-Placeholder")}
            /> */}

            <Controller
              name={params.row._id}
              key={params.row._id}
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <NumericFormat
                  name={params.row._id}
                  key={params.row._id}
                  customInput={TextField}
                  thousandSeparator=","
                  decimalScale={2}
                  allowNegative={true}
                  allowLeadingZeros={false}
                  fixedDecimalScale
                  value={field.value ?? ""}
                  onValueChange={(values) => {
                    field.onChange(values.floatValue ?? null);
                  }}
                  onBlur={(v) => {
                    const value = getValues(params.row._id);

                    if (
                      !errors[params.row._id] &&
                      value != null &&
                      value !== "" &&
                      !isNaN(Number(value))
                    ) {
                      if (updateValue) updateValue(params.row, value);
                    }
                  }}
                  placeholder={t("Enter-Value-Placeholder")}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                  }}
                  onKeyUp={async (event: React.KeyboardEvent) => {
                    switch (event.key) {
                      case "Enter":
                        (document.activeElement as HTMLElement).blur();
                        break;
                      default:
                        break;
                    }
                  }}
                />
              )}
            />
          </Box>
        );
      },
    },
    {
      field: "unit",
      headerName: t("Unit"),
      headerAlign: "left",
      minWidth: 140,
    },
  ];

  const [economicIndicatorValueResult, setEconomicIndicatorValueResult] =
    useState<EconomicIndicatorValueResult | undefined>(undefined);

  const updateValue = async (
    row: EconomicIndicatorComponent,
    value: string,
  ) => {
    let request: EcoEffActualRequest = {
      components: [],
    };

    const oldValue =
      economicIndicatorValueResult?.yearly[0].components.find(
        (e) => e.economiccomponentsRef === row._id,
      )?.value ?? undefined;

    if (Number(value) !== Number(oldValue)) {
      request.components.push({
        componentRef: row._id,
        ecoEffActuals: [
          {
            year: dayjs(editData!.startDate).year(),
            fixedValue: Number(value),
          },
        ],
      });

      alertService.showLoading();

      try {
        const result = await EcoEfficiencyService.updateEcoefficiencyActual(
          request,
          editData!._id,
        );

        if (result) {
          getEcoEfficiency();
          alertService.mixin(t("edit-successfully"));
        }
      } catch {
        // error alert already shown by service
      } finally {
        alertService.closeLoading();
      }
    }
  };

  const onSubmit = async (value: "monthly" | "yearly") => {
    try {
      const update = await EcoEfficiencyService.updateEcoEfficiency(
        editData!._id,
        {
          periodType: value,
        },
      );

      if (update) {
        setEcoEfficiency(update.ecoEfficiencies);

        setEditData(
          update.ecoEfficiencies.find((e) => e._id === editData!._id) ?? editData,
        );
        alertService.mixin(t("Save-Settings"));
      }
    } catch {
      // error alert already shown by service
    }
  };

  const getEcoefficiencyActuals = async () => {
    try {
      const economicIndicatorValueResult =
        await EcoEfficiencyService.getEcoefficiencyActuals(editData!._id);

      if (economicIndicatorValueResult) {
        setEconomicIndicatorValueResult(economicIndicatorValueResult);
        //รายปี
        economicIndicatorValueResult.yearly[0].components.forEach((c) =>
          setValue(
            c.economiccomponentsRef,
            c.value,
            // NumberFormatterUtils.numberFormat(c.value)
          ),
        );

        setMonthly(economicIndicatorValueResult.monthly);
      }
    } catch {
      // error alert already shown by service
    }
  };

  useEffect(() => {
    if (!editData || section !== "Add-Actual-Data") return;

    setRows(editData!.economicindicatorsRef.components);

    setPeriodType(editData!.periodType);

    getEcoefficiencyActuals();
  }, [editData]);

  useEffect(() => {
    const item = ecoEfficiency.find((e) => e._id === editData?._id);

    if (item) setEditData(item);
  }, [ecoEfficiency]);

  return (
    <>
      <Grid2 container disableEqualOverflow rowSpacing={"20px"}>
        <Grid2 xs={12}>
          <LabelInput
            label={t("Actual-Product-Value-Indicator")}
            info={t("Actual-Product-Value-Indicator-Info")}
            showRequired={false}
          />
        </Grid2>
        <Grid2 xs={12}>
          <Typography variant="body1">
            {t("Select-Dashboard-Display-Type")}
          </Typography>
        </Grid2>
        <RadioGroup
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          name="row-radio-buttons-group"
          sx={{
            width: "100%",
          }}
          value={periodType}
          onChange={handlePeriodTypeChange}
        >
          <Grid2 xs={12}>
            <Box
              sx={{
                borderRadius: "8px",
                border: "1px solid var(--gray2)",
                padding: "20px",
              }}
            >
              <FormControlLabel
                control={<Radio />}
                label={t("Yearly")}
                value="yearly"
              />

              <Box paddingX={"32px"} marginTop={"8px"}>
                <Typography
                  variant="body1"
                  bgcolor={"var(--gray3)"}
                  padding={"12px"}
                  display={"flex"}
                  alignItems={"center"}
                  gap={"4px"}
                  borderRadius={"4px"}
                  color={"var(--green)"}
                >
                  <InfoOutlinedIcon color="primary" />{" "}
                  {t("Dashboard-Yearly-Only-Note")}
                </Typography>
              </Box>

              <Box
                paddingX={"32px"}
                marginTop={"20px"}
                sx={{
                  opacity: periodType === "yearly" ? 1 : 0.5,
                  pointerEvents: periodType === "yearly" ? "auto" : "none",
                }}
              >
                <AppDataGrid
                  rows={rows}
                  columns={columns}
                  rowHeight={90}
                  sx={{ ...tableStyle() }}
                  getRowId={(row) => row._id}
                  disableMultipleRowSelection={true}
                  hideFooter
                  autoHeight
                />
              </Box>
            </Box>
          </Grid2>

          <Grid2 xs={12}>
            <Box
              sx={{
                borderRadius: "8px",
                border: "1px solid var(--gray2)",
                padding: "20px",
                marginTop: "20px",
              }}
            >
              <FormControlLabel
                control={<Radio />}
                label={t("Monthly")}
                value="monthly"
              />

              <Box
                sx={{
                  padding: "20px",
                  opacity: periodType === "monthly" ? 1 : 0.5,
                  pointerEvents: periodType === "monthly" ? "auto" : "none",
                }}
              >
                <ActualDataMonth monthlyValue={monthly} />
              </Box>
            </Box>
          </Grid2>
        </RadioGroup>

        <Grid2 xs={12} textAlign={"right"}>
          <Button
            variant="contained"
            color="secondary"
            sx={{ width: "140px", marginTop: "20px" }}
            type="button"
            onClick={() => {
              setSection("Details");
              setModalIsOpen(false);
            }}
          >
            {t("Back")}
          </Button>
        </Grid2>
      </Grid2>
    </>
  );
};
